import React, { useEffect, useMemo, useState, useCallback } from 'react';
import AppShell from '@/components/layout/AppShell';
import { ownerNav } from '@/lib/navConfig';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { UserPlus, Trash2, Edit2, Search, Shield, User as UserIcon, Wallet, Briefcase, PiggyBank } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/lib/AuthContext';

const ROLES = [
  { value: 'owner', label: 'Owner', className: 'bg-violet-100 text-violet-700 hover:bg-violet-100', icon: Shield },
  { value: 'admin', label: 'Admin', className: 'bg-sky-100 text-sky-700 hover:bg-sky-100', icon: Briefcase },
  { value: 'kasir', label: 'Kasir', className: 'bg-teal-100 text-teal-700 hover:bg-teal-100', icon: Wallet },
  { value: 'salesman', label: 'Salesman', className: 'bg-amber-100 text-amber-700 hover:bg-amber-100', icon: UserIcon },
  { value: 'finance', label: 'Finance', className: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100', icon: PiggyBank },
];
const roleMeta = (r) => ROLES.find((x) => x.value === r) || { label: r, className: '', icon: UserIcon };

export default function OwnerUsers() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/users');
      setUsers(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal memuat user'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let list = users;
    if (roleFilter !== 'all') list = list.filter((u) => u.role === roleFilter);
    if (query) {
      const q = query.toLowerCase();
      list = list.filter((u) => u.username.toLowerCase().includes(q) || u.full_name.toLowerCase().includes(q));
    }
    return list;
  }, [users, query, roleFilter]);

  const counts = useMemo(() => {
    const c = { owner: 0, admin: 0, kasir: 0, salesman: 0, finance: 0 };
    users.forEach((u) => { if (c[u.role] !== undefined) c[u.role] += 1; });
    return c;
  }, [users]);

  const doDelete = async (u) => {
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`User "${u.full_name}" (@${u.username}) dihapus`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal menghapus'); }
  };

  return (
    <AppShell nav={ownerNav} title="Manajemen User" showBell>
      <div className="space-y-4">
        {/* Header + counters */}
        <div className="flex flex-wrap gap-2 items-center">
          {ROLES.map((r) => (
            <Badge key={r.value} className={`${r.className} border-0 px-3 py-1`} data-testid={`role-count-${r.value}`}>
              <r.icon className="h-3.5 w-3.5 mr-1" />
              {r.label}: <b className="ml-1 tabular-nums">{counts[r.value] || 0}</b>
            </Badge>
          ))}
        </div>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div className="flex flex-col sm:flex-row gap-2 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Cari nama atau username..." value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9" data-testid="user-search-input" />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-full sm:w-40" data-testid="user-role-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Role</SelectItem>
                {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
            <DialogTrigger asChild>
              <Button data-testid="owner-add-user-button"><UserPlus className="h-4 w-4 mr-2" />Tambah User</Button>
            </DialogTrigger>
            <UserFormDialog editing={editing} onSaved={() => { load(); setOpen(false); setEditing(null); }} />
          </Dialog>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table data-testid="users-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Nama</TableHead>
                    <TableHead>Username</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Telepon</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Aksi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-sm text-muted-foreground">Tidak ada user cocok</TableCell></TableRow>
                  ) : filtered.map((u) => {
                    const rm = roleMeta(u.role);
                    const isSelf = currentUser?.id === u.id;
                    return (
                      <TableRow key={u.id} data-testid={`user-row-${u.username}`}>
                        <TableCell className="font-medium">
                          {u.full_name}
                          {isSelf && <span className="ml-2 text-[10px] text-muted-foreground">(Anda)</span>}
                        </TableCell>
                        <TableCell className="font-mono text-xs">@{u.username}</TableCell>
                        <TableCell>
                          <Badge className={`${rm.className} border-0`}>
                            <rm.icon className="h-3 w-3 mr-1" />
                            {rm.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">{u.phone || '-'}</TableCell>
                        <TableCell>
                          {u.active
                            ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-0">Aktif</Badge>
                            : <Badge variant="secondary">Nonaktif</Badge>}
                        </TableCell>
                        <TableCell className="text-right space-x-1">
                          <Button size="sm" variant="outline" onClick={() => { setEditing(u); setOpen(true); }} data-testid={`owner-edit-user-${u.username}`}>
                            <Edit2 className="h-3.5 w-3.5 mr-1" />Edit
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                size="sm" variant="outline"
                                className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 border-rose-200"
                                disabled={isSelf}
                                title={isSelf ? 'Tidak bisa menghapus akun sendiri' : 'Hapus user'}
                                data-testid={`owner-delete-user-${u.username}`}
                              >
                                <Trash2 className="h-3.5 w-3.5 mr-1" />Hapus
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent data-testid={`delete-user-confirm-${u.username}`}>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Hapus user "{u.full_name}"?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  User akan dihapus permanen dari sistem dan tidak bisa lagi login.
                                  <br /><br />
                                  <b>Username:</b> @{u.username}<br />
                                  <b>Role:</b> {rm.label}<br />
                                  {u.phone && <><b>Telepon:</b> {u.phone}<br /></>}
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Batal</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => doDelete(u)}
                                  className="bg-rose-600 hover:bg-rose-700"
                                  data-testid={`delete-user-confirm-btn-${u.username}`}
                                >
                                  Ya, Hapus
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}

function UserFormDialog({ editing, onSaved }) {
  const [username, setUsername] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState('salesman');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [active, setActive] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (editing) {
      setUsername(editing.username);
      setFullName(editing.full_name);
      setRole(editing.role);
      setPassword('');
      setPhone(editing.phone || '');
      setActive(editing.active);
    } else {
      setUsername(''); setFullName(''); setRole('salesman'); setPassword(''); setPhone(''); setActive(true);
    }
  }, [editing]);

  const submit = async () => {
    // Validation
    if (!editing) {
      if (!username.trim()) return toast.error('Username wajib diisi');
      if (!/^[a-zA-Z0-9_.-]{3,32}$/.test(username.trim())) return toast.error('Username: 3-32 karakter (huruf/angka/_.-)');
      if (!password || password.length < 6) return toast.error('Password minimal 6 karakter');
    } else if (password && password.length < 6) {
      return toast.error('Password minimal 6 karakter');
    }
    if (!fullName.trim()) return toast.error('Nama lengkap wajib diisi');

    setSubmitting(true);
    try {
      if (editing) {
        const body = { full_name: fullName.trim(), phone: phone.trim(), active, role };
        if (password) body.password = password;
        await api.patch(`/users/${editing.id}`, body);
        toast.success('User diperbarui');
      } else {
        await api.post('/users', {
          username: username.trim(),
          password,
          full_name: fullName.trim(),
          role,
          phone: phone.trim(),
          active,
        });
        toast.success(`User "${fullName}" (${role}) berhasil dibuat`);
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal simpan');
    } finally { setSubmitting(false); }
  };

  return (
    <DialogContent className="sm:max-w-md" data-testid="user-form-dialog">
      <DialogHeader><DialogTitle>{editing ? 'Edit User' : 'Tambah User Baru'}</DialogTitle></DialogHeader>
      <div className="space-y-3 py-2">
        <div className="space-y-1.5">
          <Label>Username <span className="text-rose-500">*</span></Label>
          <Input
            value={username}
            onChange={(e) => setUsername(e.target.value.toLowerCase())}
            disabled={!!editing}
            placeholder="mis. bambang"
            data-testid="user-form-username"
          />
          {!editing && <p className="text-[10px] text-muted-foreground">Huruf kecil, angka, dan simbol ( _ . - ), 3-32 karakter.</p>}
        </div>
        <div className="space-y-1.5">
          <Label>Nama Lengkap <span className="text-rose-500">*</span></Label>
          <Input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="mis. Bambang Susanto" data-testid="user-form-fullname" />
        </div>
        <div className="space-y-1.5">
          <Label>Role <span className="text-rose-500">*</span></Label>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ROLES.map((r) => (
                <SelectItem key={r.value} value={r.value} data-testid={`user-form-role-${r.value}`}>
                  <div className="flex items-center gap-2">
                    <r.icon className="h-3.5 w-3.5" />
                    {r.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {editing && role !== editing.role && (
            <p className="text-[10px] text-amber-700 bg-amber-50 rounded px-2 py-1">
              Peringatan: mengubah role akan memindahkan hak akses user ini. Pastikan sudah tepat.
            </p>
          )}
        </div>
        <div className="space-y-1.5">
          <Label>Password {editing
            ? <span className="text-xs text-muted-foreground">(kosongkan jika tidak diubah)</span>
            : <span className="text-rose-500">*</span>}
          </Label>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min. 6 karakter" data-testid="user-form-password" />
        </div>
        <div className="space-y-1.5">
          <Label>Telepon</Label>
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="mis. 08123456789" data-testid="user-form-phone" />
        </div>
        <div className="flex items-center justify-between rounded-lg border p-3">
          <div>
            <Label className="text-sm">Status Aktif</Label>
            <p className="text-[10px] text-muted-foreground">User nonaktif tidak bisa login</p>
          </div>
          <Switch checked={active} onCheckedChange={setActive} data-testid="user-form-active" />
        </div>
      </div>
      <DialogFooter>
        <Button onClick={submit} disabled={submitting} data-testid="user-form-save">
          {submitting ? 'Menyimpan...' : (editing ? 'Simpan Perubahan' : 'Buat User')}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
